import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import keyBy from 'lodash/keyBy';

import enhanceWithCohort from 'hocs/enhanceWithCohort';
import enhanceWithSubscription from 'hocs/enhanceWithSubscription';

import {
  AutoCompleteSingleInput,
  PopperDialog,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';

import {
  PageWrapper,
  PageViewport,
  PageContent,
  PageActions,
  PageActionsRight,
} from 'components/PageStyles';
import { AccountRow } from 'components/AutoCompleteSuggestionItems';

import Sidebar from 'Workspace/components/WorkspaceSidebar';
import CohortNavigation from 'Workspace/Cohorts/components/CohortNavigation';
import cohortShape from 'Workspace/types/cohorts/cohort';
import { accountShape } from 'Workspace/types/account';
import AccountHelper from 'Workspace/helpers/AccountHelper';
import hardCodedWorkspaceConfig from 'Workspace/Cohorts/hardcoded-config-by-workspace.json';
import gtmService from 'utils/gtm/gtmService';

import CohortPageHeader from '../CohortPageHeader';
import EnrollmentsButton from './EnrollmentsButton';
import CohortEnrollmentsList from '../CohortEnrollmentsList';
import { commandRevokePermissionOnCohort,
} from 'containers/ConnectionManager/actions';

import messages from './messages';

export const filterFn = (data, value) => {
  const input = String(value).toLowerCase();
  return data.filter((item) => item.text.toLowerCase().indexOf(input) >= 0);
};

class CohortEnrollments extends Component {
  state = {
    enrollmentPopperOpened: false,
  }

  componentDidMount() {
    const { match: { params: { cohortId } } } = this.props;

    this.props.getCohortCollaboratorsSummary(cohortId);
    this.props.getCohortEnrollments(cohortId);

    if (this.props.location) {
      const { pathname } = this.props.location;
      gtmService.pushTelemetryPageActivityAndView({ pageName: pathname });
    }
  }

  normalizeUsersList = (usersList = []) => usersList
    .map((item) => ({
      id: item.accountId,
      text: AccountHelper.getFullName(item),
      data: item,
    }))

  openEnrollmentPopper = () => {
    this.setState({ enrollmentPopperOpened: true });
  }

  closeEnrollmentPopper = () => {
    this.setState({ enrollmentPopperOpened: false });
  }

  render() {
    const {
      cohort,
      subscriptionAccountList,
      updateCohort,
      enrollStudent,
      match: {
        params: { cohortId },
      },
      revokePermission,
    } = this.props;

    const { enrollmentPopperOpened } = this.state;

    const enrollments = (cohort && cohort.enrollments) || [];
    const enrollmentsById = keyBy(enrollments, 'accountSummary.accountId');

    const usersList = this.normalizeUsersList(subscriptionAccountList).filter((item) => !enrollmentsById[item.id]);
    const workspaceConfig = cohort && cohort.summary && hardCodedWorkspaceConfig[cohort.summary.workspaceId];
    const manualEnrollmentsEnabled = !(workspaceConfig && workspaceConfig.disableManualEnrollment);

    return (
      <PageWrapper>
        <Sidebar navigation={<CohortNavigation cohortId={cohortId} selectedKey="enrollments" />} />
        <PageViewport>
          {cohort && cohort.summary &&
          <Fragment>
            <CohortPageHeader
              cohort={cohort}
              title={intl.formatMessage(messages.title)}
              updateCohort={updateCohort}
              onRevoke={(account)=>{revokePermission(account, cohortId);}}
            />
            { manualEnrollmentsEnabled && (
              <PageActions>
                <PageActionsRight>
                  <EnrollmentsButton onClick={this.openEnrollmentPopper}>
                    {
                      enrollmentPopperOpened &&
                        <PopperDialog
                          title={intl.formatMessage(messages.enrollStudents)}
                          onClose={this.closeEnrollmentPopper}
                        >
                          <AutoCompleteSingleInput
                            data={usersList}
                            filterFunction={filterFn}
                            label={intl.formatMessage(messages.enrollStudents)}
                            placeholder={intl.formatMessage(messages.enrollStudentsPlaceholder)}
                            noResultsText={intl.formatMessage(messages.noResults)}
                            onSelect={(account) => enrollStudent(account.id, cohortId)}
                            renderSuggestionItem={
                              (data) => <AccountRow data={data} />
                            }
                          />
                        </PopperDialog>
                    }
                  </EnrollmentsButton>
                </PageActionsRight>
              </PageActions>
            )}
            <PageContent>
              { enrollments.length > 0 && (
                <CohortEnrollmentsList
                  enrollments={cohort.enrollments}
                />
              )}
            </PageContent>
          </Fragment>
          }
        </PageViewport>
      </PageWrapper>
    );
  }
}

CohortEnrollments.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      cohortId: PropTypes.string,
    }),
  }),
  cohort: cohortShape,
  getCohortCollaboratorsSummary: PropTypes.func,
  enrollStudent: PropTypes.func,
  getCohortEnrollments: PropTypes.func,
  subscriptionAccountList: PropTypes.arrayOf(accountShape),
  updateCohort: PropTypes.func,
  location: PropTypes.object,
};

const withCohort = enhanceWithCohort(({ match }) => match.params.cohortId);

const mapDispatchToProps = (dispatch) => ({
  revokePermission: (account, cohortId) => dispatch(commandRevokePermissionOnCohort(account.id, account.type, cohortId)),
});

export default connect(null, mapDispatchToProps)(withCohort(enhanceWithSubscription(CohortEnrollments)));
